import type { TagCatalogQuery, TagDefinition } from "../api/generated/model";

// Estado del catálogo en esta pestaña (TagCatalogViewState del portal actual): la consulta vive en la dirección; la
// selección y el desplazamiento se conservan mientras dure la app, aislados por empresa y persona.

export const defaultQuery: TagCatalogQuery = {
  search: null,
  state: "ACTIVE",
  from: null,
  to: null,
  sort: "CREATED",
  page: 1,
  // Escritorio: 10 por página, la primera opción de «Por página» (decisión del dueño, 29 oct 2026).
  pageSize: 10,
  selectedIds: null,
  selectedNames: null,
  reverse: false,
};

const isDate = (value: string | null) =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value));

/** Consulta desde la dirección (q, state, from, to, order, rev, p, n), con los mismos valores por omisión. */
export function parseQuery(search: string): TagCatalogQuery {
  const params = new URLSearchParams(search);
  const number = (key: string, fallback: number) => {
    const value = Number(params.get(key));
    return params.has(key) && Number.isInteger(value) ? value : fallback;
  };
  return {
    ...defaultQuery,
    search: params.get("q"),
    state: params.get("state") ?? "ACTIVE",
    from: isDate(params.get("from")) ? params.get("from") : null,
    to: isDate(params.get("to")) ? params.get("to") : null,
    sort: ["CREATED", "NAME", "USED"].includes(params.get("order") ?? "") ? params.get("order")! : "CREATED",
    reverse: params.get("rev") === "1",
    page: number("p", 1),
    pageSize: number("n", 10),
  };
}

/** Dirección de una consulta (Url() del portal actual): mismos parámetros, en el mismo orden. */
export function queryUrl(query: TagCatalogQuery): string {
  const pairs: [string, string | null | undefined][] = [
    ["q", query.search],
    ["state", query.state],
    ["from", query.from],
    ["to", query.to],
    ["order", query.sort],
    ["rev", query.reverse ? "1" : null],
    ["p", String(query.page)],
    ["n", String(query.pageSize)],
  ];
  return (
    "/portal/chat/configuracion/etiquetas?" +
    pairs
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([name, value]) => `${name}=${encodeURIComponent(value!)}`)
      .join("&")
  );
}

type Store = {
  owner: string;
  sessionStamp: string | null;
  selected: Map<number, TagDefinition>;
  scroll: number;
  query: TagCatalogQuery;
};
const store: Store = { owner: "", sessionStamp: null, selected: new Map(), scroll: 0, query: { ...defaultQuery } };

/** El estado del catálogo de esta persona en esta empresa (otra empresa o persona lo vacía). */
export function catalogState(tenant: number, user: number): Store {
  const owner = `${tenant}:${user}`;
  if (store.owner !== owner) clearCatalogState(owner);
  return store;
}

/** Guarda parte del estado del catálogo (selección, consulta, sesión o desplazamiento) de la persona actual. */
export function rememberCatalog(patch: Partial<Omit<Store, "owner">>): void {
  Object.assign(store, patch);
}

export function clearCatalogState(owner = ""): void {
  store.owner = owner;
  store.sessionStamp = null;
  store.selected.clear();
  store.scroll = 0;
  store.query = { ...defaultQuery };
}

/** Igualdad de consultas sin contar página, tamaño ni orden (cambiar esos no limpia la selección). */
export function sameFilters(a: TagCatalogQuery, b: TagCatalogQuery): boolean {
  const strip = (query: TagCatalogQuery) =>
    JSON.stringify({ ...query, page: 1, pageSize: 25, sort: "CREATED", reverse: false, selectedIds: null, selectedNames: null });
  return strip(a) === strip(b);
}
